import { useState, useEffect, ReactElement } from 'react';
import { Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { queryKeysClient } from 'common/clients/query-key.client';
import { queryClient } from 'common/clients/query.client';
import { Button } from 'common/components/antd/Button/Button';
import { Form } from 'common/components/antd/Form/Form';
import { Input } from 'common/components/antd/Input/Input';
import { Select } from 'common/components/antd/Select/Select';
import { Space } from 'common/components/antd/Space/Space';
import { EnhancedModal, Body, Footer, Header } from 'common/components/EnhancedModal/EnhancedModal';
import { ENV_COUNTERPARTY_GUID } from 'common/constants/env.constants';
import { EQueryKeys } from 'common/enums/query-kyes.enum';
import { IBANFormItemRules } from 'common/utils/form.utils';
import { notifySuccess } from 'common/utils/notifier.utils';
import { useCreateAccountMutation, useCreateBranchAccountMutation } from 'domains/accounts/queries/accounts.query';
import { TCreateAccountReqParams, EAccountKind } from 'domains/accounts/types/accounts.types';
import { useGetAssetsListQuery } from 'domains/assets/queries/assets.query';
import { useGetBranchesListQuery } from 'domains/branches/queries/branch.query';
import { TCounterpartyBranchEntity } from 'domains/branches/types/branch.types';
import {
  SFooter,
} from './modal.style';
import { EModalType } from '../enums/counerparty-processing.enums';
import { TCounterpartyProcessingModalData } from '../types/counterparty-processing.types';

enum EFormFieldsName {
  CounterpartyGuid = 'counterparty_guid',
  AssetIssuer = 'asset_issuer',
  Branch = 'branch',
  ReserveAccountBalance = 'reserve_account_balance',
  KeyKeeperLogin = 'key_keeper_login',
  IBAN = 'iban',
}

type TProps = {
  isOpen?: boolean;
  onCancelModalHandler?: () => void;
  modalData: TCounterpartyProcessingModalData;
};

const CreateEditProcessingAccountModal = (props: TProps): ReactElement => {
  const {
    isOpen,
    onCancelModalHandler,
    modalData,
  } = props;
  const [form] = Form.useForm();
  const { t } = useTranslation();
  const [isSubmitBtnLoading, setIsSubmitBtnLoading] = useState<boolean>(false);

  useEffect(() => {
    form.setFields([
      {
        name: EFormFieldsName.CounterpartyGuid,
        value: modalData?.rowData?.selectedCounterparty,
      }
    ]);

    if (Object.keys(modalData?.rowData?.record || {}).length) {
      form.setFields([
        {
          name: EFormFieldsName.AssetIssuer,
          value: modalData?.rowData?.record?.currency,
        },
        {
          name: EFormFieldsName.IBAN,
          value: modalData?.rowData?.record?.iban,
        },
        {
          name: EFormFieldsName.ReserveAccountBalance,
          value: modalData?.rowData?.record?.reserve_acc,
        },
        {
          name: EFormFieldsName.Branch,
          value: modalData?.rowData?.record?.branch?.name,
        }
      ]);
    }
  }, [modalData]);

  const {
    data: branchesData,
    isError: isBranchesDataError,
    error: branchesDataError,
  } = useGetBranchesListQuery(
    modalData?.rowData?.selectedCounterparty,
    {},
    {
      enabled: !!modalData?.rowData?.selectedCounterparty,
    },
  );

  const {
    data: assetsData,
    isError: isAssetsDataError,
    error: assetsDataError,
  } = useGetAssetsListQuery(
    ENV_COUNTERPARTY_GUID,
    {},
    {
      enabled: !!modalData?.rowData?.selectedCounterparty,
      refetchOnMount: false,
    },
  );

  const isError = isAssetsDataError || isBranchesDataError;
  const errorData = assetsDataError || branchesDataError;

  useEffect(() => {
    if (isError) {
      console.error(errorData);
    }
  }, [isError, errorData]);

  const {
    mutateAsync: mutateAsyncCreateAccountMutation,
  } = useCreateAccountMutation();

  const {
    mutateAsync: mutateAsyncCreateBranchAccountMutation,
  } = useCreateBranchAccountMutation();

  const onFormSubmitHandler = async (values: Record<EFormFieldsName, any>): Promise<void> => {
    try {
      setIsSubmitBtnLoading(true);

      const {
        counterparty_guid: counterpartyGuid,
        // asset_issuer,
        // reserve_account,
        // reserve_account_balance,
        key_keeper_login,
        iban,
        branch,
      } = values;

      const reqData: TCreateAccountReqParams = {
        account_kind: EAccountKind.Processing,
        branch_guid: branch,
        iban,
        key_keeper_login,
      };

      if (modalData.type === EModalType.Create) {
        if (branch) {
          await mutateAsyncCreateBranchAccountMutation({
            counterpartyGUID: counterpartyGuid,
            branchGUID: branch,
            requestData: reqData,
          });
        } else {
          await mutateAsyncCreateAccountMutation({
            counterpartyGUID: counterpartyGuid,
            requestData: reqData,
          });
        }
        notifySuccess('accountManagement.counterpartyProcessing.modal.create.notification.success');
      } else {
        // TODO: need API for Edit
        notifySuccess('accountManagement.counterpartyProcessing.modal.create.notification.update.success');
      }

      await queryClient.invalidateQueries({
        queryKey: queryKeysClient[EQueryKeys.AccountQueryKeys].getCounterpartyAccounts._def,
      });

      if (onCancelModalHandler) {
        onCancelModalHandler();
      }

      setIsSubmitBtnLoading(false);
    } catch (error) {
      console.error(error);
      setIsSubmitBtnLoading(false);
    }
  };

  const branchOptions = (branchesData?.list || []).map((item: TCounterpartyBranchEntity) => ({
    label: item.name,
    value: item.guid,
  }));

  const assetOptions =
    assetsData?.stellar_assets?.map((asset) => ({
      label: asset.asset_code,
      value: asset.asset_issuer,
    })) || [];

  const afterCloseHandler = (): void => {
    form.setFields([
      {
        name: EFormFieldsName.CounterpartyGuid,
        value: '',
      },
      {
        name: EFormFieldsName.Branch,
        value: '',
      },
      {
        name: EFormFieldsName.AssetIssuer,
        value: '',
      },
      {
        name: EFormFieldsName.IBAN,
        value: '',
      },
      {
        name: EFormFieldsName.ReserveAccountBalance,
        value: '',
      },
      {
        name: EFormFieldsName.KeyKeeperLogin,
        value: '',
      },
    ]);
  }

  return (
    <EnhancedModal
      open={isOpen}
      width={480}
      onCancel={onCancelModalHandler}
      afterClose={afterCloseHandler}
    >
      <Form form={form} onFinish={onFormSubmitHandler}>
        <Header>
          {(modalData.type === EModalType.Create)
            ? t('accountManagement.counterpartyProcessing.modal.create.title')
            : t('accountManagement.counterpartyProcessing.modal.edit.title')
          }
        </Header>
        <Body>
          <div>
            <div>{t('accountManagement.counterpartyProcessing.modal.create.accountType.label')}</div>
            <Typography.Text
              strong
              style={{
                marginTop: 8,
              }}
            >
              {t('accountManagement.counterpartyProcessing.modal.create.accountType.value')}
            </Typography.Text>
          </div>
          <Space
            size={16}
            direction="vertical"
            style={{
              marginTop: 24,
            }}
          >
            <Form.Item
              name={EFormFieldsName.CounterpartyGuid}
              label={t('accountManagement.counterpartyProcessing.modal.create.counterparty.label')}
            >
              <Select
                size="large"
                style={{ width: '100%' }}
                disabled
                placeholder={t('accountManagement.counterpartyProcessing.modal.create.counterparty.placeholder')}
                options={modalData?.rowData?.counterpartyOptions}
              />
            </Form.Item>
            <Form.Item
              name={EFormFieldsName.Branch}
              label={t('accountManagement.counterpartyProcessing.modal.create.branch.label')}
            >
              <Select
                size="large"
                style={{ width: '100%' }}
                placeholder={t('accountManagement.counterpartyProcessing.modal.create.branch.placeholder')}
                options={branchOptions}
                allowClear
              />
            </Form.Item>
            <Form.Item
              name={EFormFieldsName.AssetIssuer}
              label={t('accountManagement.counterpartyProcessing.modal.create.currency.label')}
            >
              <Select
                size="large"
                style={{ width: '100%' }}
                placeholder={t('accountManagement.counterpartyProcessing.modal.create.currency.placeholder')}
                options={assetOptions}
              />
            </Form.Item>
            <Form.Item
              name={EFormFieldsName.IBAN}
              label={t('accountManagement.counterpartyProcessing.modal.create.iban.label')}
              rules={IBANFormItemRules}
            >
              <Input
                size="large"
                placeholder={t('accountManagement.counterpartyProcessing.modal.create.iban.placeholder') as string}
              />
            </Form.Item>
            {modalData.type === EModalType.Create && (
              <Form.Item
                name={EFormFieldsName.KeyKeeperLogin}
                label={t('accountManagement.counterpartyProcessing.modal.create.keyKeeperLogin.label')}
                rules={[{ required: true }]}
              >
                <Input
                  size="large"
                  placeholder=
                    {t('accountManagement.counterpartyProcessing.modal.create.keyKeeperLogin.placeholder') as string}
                />
              </Form.Item>
            )}
          </Space>
        </Body>
        <Footer>
          <SFooter>
            <Button
              size="large"
              style={{
                marginRight: 12,
              }}
              onClick={onCancelModalHandler}
            >
              {t('accountManagement.counterpartyProcessing.modal.create.button.cancel')}
            </Button>
            <Button
              size="large"
              loading={isSubmitBtnLoading}
              htmlType="submit"
              type="primary"
            >
              {(modalData.type === EModalType.Create)
                ? t('accountManagement.counterpartyProcessing.modal.create.button.create')
                : t('accountManagement.counterpartyProcessing.modal.edit.button.create')
              }
            </Button>
          </SFooter>
        </Footer>
      </Form>
    </EnhancedModal>
  );
};

export default CreateEditProcessingAccountModal;
